import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import * as React from 'react';
import { updateCSS, removeCSS } from "rc-util/es/Dom/dynamicCSS";
import canUseDom from "rc-util/es/Dom/canUseDom";
import hash from '@emotion/hash'; // @ts-ignore

import unitless from '@emotion/unitless';
import { compile, serialize, stringify } from 'stylis';
import useGlobalCache from './useGlobalCache';
import StyleContext, { ATTR_MARK, ATTR_TOKEN, ATTR_DEV_CACHE_PATH, CSS_IN_JS_INSTANCE, CSS_IN_JS_INSTANCE_ID } from './StyleContext';
import { styleValidate } from './util';
var isClientSide = canUseDom();
var SKIP_CHECK = '_skip_check_'; // ============================================================================
// ==                                 Parser                                 ==
// ============================================================================
// Preprocessor style content to browser support one

function normalizeStyle(styleStr) {
  return serialize(compile(styleStr), stringify);
}

function isCompoundCSSProperty(value) {
  return _typeof(value) === 'object' && value && SKIP_CHECK in value;
}

export var animationStatistics = {}; // Parse CSSObject to style content

export var parseStyle = function parseStyle(interpolation, hashId, path) {
  var root = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var injectHash = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var styleStr = '';

  function parseKeyframes(keyframes) {
    if (animationStatistics[keyframes.getName(hashId)]) {
      return '';
    }

    animationStatistics[keyframes.getName(hashId)] = true;
    return "@keyframes ".concat(keyframes.getName(hashId)).concat(parseStyle(keyframes.style, hashId, path, false));
  }

  function flattenList(list) {
    var fullList = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    list.forEach(function (item) {
      if (Array.isArray(item)) {
        flattenList(item, fullList);
      } else if (item) {
        fullList.push(item);
      }
    });
    return fullList;
  }

  var flattenStyleList = flattenList(Array.isArray(interpolation) ? interpolation : [interpolation]);
  flattenStyleList.forEach(function (style) {
    if (style._keyframe) {
      // Keyframe
      styleStr += parseKeyframes(style);
    } else {
      // Normal CSSObject
      Object.keys(style).forEach(function (key) {
        var value = style[key];

        if (_typeof(value) === 'object' && value && (key !== 'animationName' || !value._keyframe) && !isCompoundCSSProperty(value)) {
          var subInjectHash = false; // 当成嵌套对象来处理

          var mergedKey = key.trim(); // 拆分多个选择器

          if ((root || injectHash) && hashId) {
            if (mergedKey.startsWith('@')) {
              // 略过媒体查询，交给子节点继续插入 hashId
              subInjectHash = true;
            } else {
              // 注入 hashId
              var keys = key.split(',').map(function (k) {
                return ".".concat(hashId).concat(k.trim());
              });
              mergedKey = keys.join(',');
            }
          }

          styleStr += "".concat(mergedKey).concat(parseStyle(value, hashId, "".concat(path, " -> ").concat(mergedKey), false, subInjectHash));
        } else {
          var _value$value;

          var actualValue = (_value$value = value === null || value === void 0 ? void 0 : value.value) !== null && _value$value !== void 0 ? _value$value : value;

          if (process.env.NODE_ENV !== 'production' && (_typeof(value) !== 'object' || !(value === null || value === void 0 ? void 0 : value[SKIP_CHECK]))) {
            styleValidate(key, actualValue, {
              path: path,
              hashId: hashId
            });
          } // 如果是样式则直接插入


          var styleName = key.replace(/[A-Z]/g, function (match) {
            return "-".concat(match.toLowerCase());
          }); // Auto suffix with px

          var formatValue = actualValue;

          if (!unitless[key] && typeof formatValue === 'number' && formatValue !== 0) {
            formatValue = "".concat(formatValue, "px");
          } // handle animationName & Keyframe value


          if (key === 'animationName' && (value === null || value === void 0 ? void 0 : value._keyframe)) {
            styleStr += parseKeyframes(value);
            formatValue = value.getName(hashId);
          }

          styleStr += "".concat(styleName, ":").concat(formatValue, ";");
        }
      });
    }
  });

  if (!root) {
    styleStr = "{".concat(styleStr, "}");
  }

  return styleStr;
}; // ============================================================================
// ==                                Register                                ==
// ============================================================================

function uniqueHash(path, styleStr) {
  return hash("".concat(path.join('%')).concat(styleStr));
}

function Empty() {
  return null;
}
/**
 * Register a style to the global style sheet.
 */


export default function useStyleRegister(info, styleFn) {
  var token = info.token,
      path = info.path,
      hashId = info.hashId;

  var _React$useContext = React.useContext(StyleContext),
      autoClear = _React$useContext.autoClear,
      mock = _React$useContext.mock,
      defaultCache = _React$useContext.defaultCache;

  var tokenKey = token._tokenKey;
  var fullPath = [tokenKey].concat(_toConsumableArray(path)); // Check if need insert style

  var isMergedClientSide = isClientSide;

  if (process.env.NODE_ENV !== 'production' && mock !== undefined) {
    isMergedClientSide = mock === 'client';
  }

  var _useGlobalCache = useGlobalCache('style', fullPath, // Create cache if needed
  function () {
    var styleObj = styleFn();
    var styleStr = normalizeStyle(parseStyle(styleObj, hashId, path.join('-')));
    var styleId = uniqueHash(fullPath, styleStr); // Clear animation statistics

    animationStatistics = {};

    if (isMergedClientSide) {
      var style = updateCSS(styleStr, styleId, {
        mark: ATTR_MARK
      });
      style[CSS_IN_JS_INSTANCE] = CSS_IN_JS_INSTANCE_ID; // Used for `useCacheToken` to remove on batch when token removed

      style.setAttribute(ATTR_TOKEN, tokenKey); // Dev usage to find which cache path made this easily

      if (process.env.NODE_ENV !== 'production') {
        style.setAttribute(ATTR_DEV_CACHE_PATH, fullPath.join('|'));
      }
    }

    return [styleStr, tokenKey, styleId];
  }, // Remove cache if no need
  function (_ref) {
    var _ref2 = _slicedToArray(_ref, 3),
        styleId = _ref2[2];

    if (autoClear && isClientSide) {
      removeCSS(styleId, {
        mark: ATTR_MARK
      });
    }
  }),
      _useGlobalCache2 = _slicedToArray(_useGlobalCache, 3),
      cachedStyleStr = _useGlobalCache2[0],
      cachedTokenKey = _useGlobalCache2[1],
      cachedStyleId = _useGlobalCache2[2];

  return function (node) {
    var styleNode;

    if (isMergedClientSide || !defaultCache) {
      styleNode = /*#__PURE__*/React.createElement(Empty, null);
    } else {
      var _objectSpread2;

      styleNode = /*#__PURE__*/React.createElement("style", _objectSpread(_objectSpread({}, (_objectSpread2 = {}, _defineProperty(_objectSpread2, ATTR_TOKEN, cachedTokenKey), _defineProperty(_objectSpread2, ATTR_MARK, cachedStyleId), _objectSpread2)), {}, {
        dangerouslySetInnerHTML: {
          __html: cachedStyleStr
        }
      }));
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, styleNode, node);
  };
} // ============================================================================
// ==                                  SSR                                   ==
// ============================================================================

export function extractStyle(cache) {
  // prefix with `style` is used for `useStyleRegister` to cache style context
  var styleKeys = Array.from(cache.cache.keys()).filter(function (key) {
    return key.startsWith('style%');
  }); // const tokenStyles: Record<string, string[]> = {};

  var styleText = '';
  styleKeys.forEach(function (key) {
    var _cache$cache$get$ = _slicedToArray(cache.cache.get(key)[1], 3),
        styleStr = _cache$cache$get$[0],
        tokenKey = _cache$cache$get$[1],
        styleId = _cache$cache$get$[2];

    styleText += "<style ".concat(ATTR_TOKEN, "=\"").concat(tokenKey, "\" ").concat(ATTR_MARK, "=\"").concat(styleId, "\">").concat(styleStr, "</style>");
  });
  return styleText;
}